import { ROOM_KEYS } from "../Components/PassengersData/PassengersData";
import { readVpRoomCardRawPwccIls } from "./resolveVpRoomCardDisplayPrice";
import {
  isVpManualAddedRoom,
  readVpSelectedAddRoomPriceIls,
} from "./vpManualAddRoom";
import { resolveVacationPackageRoomMarkupIls } from "./vacationPackageRoomAgentMarkup";

const LS_ROOM_SUM = "DEBUG_VP_TOTAL_PAYMENT_ROOM_SUM";
const LS_RECOMPUTE = "DEBUG_VP_TOTAL_PAYMENT_RECOMPUTE";
const LS_HANDLE_FEE = "DEBUG_VP_HANDLE_FEE_RECOMPUTE";

/** Sum only components — avoid double-counting priceWithHandleFee / PWCC in ourPrice. */
const VP_BILLABLE_COMPONENT_KEYS = [
  "initialPrice",
  "netPrice",
  "hrCommission",
  "agentCommission",
  "priceWithCommission",
  "handleFee",
  "creditCardFee",
];

/** @type {Record<string, string>} */
const _lastRoomSumFp = Object.create(null);
/** @type {string|null} */
let _lastRecomputeFp = null;
/** @type {string|null} */
let _lastHandleFeeFp = null;

function _isDev() {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

function _verboseRoomSum() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_ROOM_SUM) === "1";
  } catch {
    return false;
  }
}

function _verboseRecompute() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_RECOMPUTE) === "1";
  } catch {
    return false;
  }
}

function _verboseHandleFee() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_HANDLE_FEE) === "1";
  } catch {
    return false;
  }
}

function _round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/** @param {object|null|undefined} slot */
export function getVpSlotOurTotalFare(slot) {
  const room = slot?.room;
  if (!room) return null;
  const fd = room.ourFareDetails ?? room[ROOM_KEYS.OUR_FARE_DETAILS];
  return fd?.ourTotalFare && typeof fd.ourTotalFare === "object"
    ? fd.ourTotalFare
    : null;
}

/** @param {object[]|null|undefined} roomsAndPaxInfo */
export function roomsAndPaxInfoHasManualAddedRooms(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return false;
  return roomsAndPaxInfo.some((slot) => isVpManualAddedRoom(slot?.room, slot));
}

/**
 * @param {object} row
 * @param {{ force?: boolean }} [opts]
 */
export function logVpTotalPaymentRoomSumTrace(row, opts = {}) {
  if (!_isDev() || !row) return;
  const key = String(row.roomIndex ?? "?");
  const fp = JSON.stringify(row);
  if (!opts.force && !_verboseRoomSum() && _lastRoomSumFp[key] === fp) return;
  _lastRoomSumFp[key] = fp;
  // eslint-disable-next-line no-console
  console.info("[VP_TOTAL_PAYMENT_ROOM_SUM_TRACE]", row);
}

/**
 * @param {object} payload
 * @param {{ force?: boolean }} [opts]
 */
export function logVpTotalPaymentRecomputeTrace(payload, opts = {}) {
  if (!_isDev() || !payload) return;
  const fp = JSON.stringify(payload);
  if (!opts.force && !_verboseRecompute() && _lastRecomputeFp === fp) return;
  _lastRecomputeFp = fp;
  // eslint-disable-next-line no-console
  console.info("[VP_TOTAL_PAYMENT_RECOMPUTE_TRACE]", payload);
}

/**
 * @param {object} payload
 * @param {{ force?: boolean }} [opts]
 */
export function logVpHandleFeeRecomputeTrace(payload, opts = {}) {
  if (!_isDev() || !payload) return;
  const fp = JSON.stringify(payload);
  if (!opts.force && !_verboseHandleFee() && _lastHandleFeeFp === fp) return;
  _lastHandleFeeFp = fp;
  // eslint-disable-next-line no-console
  console.info("[VP_HANDLE_FEE_RECOMPUTE_TRACE]", payload);
}

/**
 * @param {Record<string, number>} ourPrice
 * @param {object[]} roomsAndPaxInfo
 * @param {(v: unknown) => boolean} isNumber
 * @param {{ handleFeeData?: object|null }} [opts]
 */
export function accumulateVpBillableRoomsIntoOurPrice(
  ourPrice,
  roomsAndPaxInfo,
  isNumber,
  opts = {}
) {
  let includedCount = 0;
  let roomPwccSumIls = 0;
  let roomsBaseTotal = 0;
  let roomLevelHandleFeesIls = 0;

  if (!Array.isArray(roomsAndPaxInfo)) {
    return {
      includedCount,
      roomTotalIls: 0,
      roomPwccSumIls: 0,
      roomsBaseTotal: 0,
      roomLevelHandleFeesIls: 0,
      roomsWithFeeTotal: 0,
    };
  }

  for (let roomIndex = 0; roomIndex < roomsAndPaxInfo.length; roomIndex++) {
    const slot = roomsAndPaxInfo[roomIndex];
    const room = slot?.room;
    const isManualAddedRoom = isVpManualAddedRoom(room, slot);
    const ourTotalFare = getVpSlotOurTotalFare(slot);
    const hasOurFareDetails = Boolean(ourTotalFare);
    const roomPwccIls = readVpRoomCardRawPwccIls(room);
    const selectedAddRoomPriceIls = readVpSelectedAddRoomPriceIls(room, slot);
    const configuredMarkupIls = resolveVacationPackageRoomMarkupIls(
      slot,
      opts.handleFeeData
    );

    let includedInTotal = false;
    let reason = "skipped_no_billable_price";
    let roomLevelFee = 0;

    if (hasOurFareDetails) {
      for (const key of VP_BILLABLE_COMPONENT_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(ourTotalFare, key)) continue;
        if (!isNumber(ourTotalFare[key])) continue;
        if (ourPrice[key] === undefined) ourPrice[key] = 0;
        ourPrice[key] += ourTotalFare[key];
      }
      const pwc = Number(ourTotalFare.priceWithCommission);
      const hf = Number(ourTotalFare.handleFee);
      if (Number.isFinite(pwc) && pwc > 0) roomsBaseTotal += pwc;
      if (Number.isFinite(hf) && hf > 0) {
        roomLevelFee = hf;
        roomLevelHandleFeesIls += hf;
      }
      const pwcc = Number(ourTotalFare.priceWithCommissionAndCreditCardFee);
      if (Number.isFinite(pwcc) && pwcc > 0) {
        roomPwccSumIls += pwcc;
      } else if (Number.isFinite(pwc) && pwc > 0) {
        roomPwccSumIls += pwc + (Number.isFinite(hf) ? hf : 0);
      }
      includedInTotal = true;
      includedCount += 1;
      reason = isManualAddedRoom
        ? "manual_room_ourFareDetails_sum"
        : "room_ourFareDetails_sum";
    } else if (selectedAddRoomPriceIls != null) {
      for (const key of [
        "priceWithCommission",
        "priceWithCommissionAndCreditCardFee",
        "priceWithCreditCardFee",
      ]) {
        if (ourPrice[key] === undefined) ourPrice[key] = 0;
        ourPrice[key] += selectedAddRoomPriceIls;
      }
      roomsBaseTotal += selectedAddRoomPriceIls;
      roomPwccSumIls += selectedAddRoomPriceIls;
      includedInTotal = true;
      includedCount += 1;
      reason = "manual_room_selectedAddRoomPriceIls_fallback";
    }

    logVpTotalPaymentRoomSumTrace({
      roomIndex,
      isManualAddedRoom,
      priceSource: room?.priceSource ?? null,
      hasOurFareDetails,
      roomPwccIls,
      selectedAddRoomPriceIls,
      configuredMarkupIls,
      includedInTotal,
      reason,
    });

    logVpHandleFeeRecomputeTrace({
      source: opts.traceSource ?? "accumulateVpBillableRooms",
      handleFee: opts.handleFeeData?.value ?? null,
      handleFeeType: opts.handleFeeData?.type ?? null,
      roomIndex,
      roomLevelFee,
      roomsBaseTotal: _round2(roomsBaseTotal),
      roomsWithFeeTotal: _round2(roomPwccSumIls),
      baggageTotal: null,
      creditCardFee: null,
      finalTotal: null,
      recomputeTriggered: Boolean(opts.recomputeTriggered),
      reason: includedInTotal ? reason : "room_not_included",
    });
  }

  roomsBaseTotal = _round2(roomsBaseTotal);
  roomPwccSumIls = _round2(roomPwccSumIls);
  roomLevelHandleFeesIls = _round2(roomLevelHandleFeesIls);

  return {
    includedCount,
    roomTotalIls: roomPwccSumIls,
    roomPwccSumIls,
    roomsBaseTotal,
    roomLevelHandleFeesIls,
    roomsWithFeeTotal: roomPwccSumIls,
  };
}
